import type { Criteria } from '@nbx/core/http';

import { bookingGuestAccess } from '@core/config/booking-guest-access.config';
import { BookingMirState } from '@core/config/booking-states.config';
import { isChannelManagedBooking } from '@core/config/booking-status.config';
import type { Booking } from '@interfaces/booking.interface';
import type { PagedResponse } from '@interfaces/response.interface';
import {
  type BookingMirWorkflow,
  MIR_REACTIVATION_NOT_ALLOWED,
} from '@services/booking-mir-workflow.service';

import { bookingStateMachinesFor } from '../builders';
import { mockDb } from '../mock-db';

function appendBookingStateHistory(booking: Booking, state: string): void {
  booking.stateHistory = [
    ...(booking.stateHistory ?? []),
    {
      state,
      changedAt: new Date().toISOString(),
    },
  ];
}

/**
 * Advances a seeded booking to a new lifecycle status: records the transition in `stateHistory`
 * (in the shared-matrix vocabulary the timeline reads) and splits it across the three machine
 * fields the list, header and calendar detail render, so a mutated booking never shows «Desconocido».
 */
function moveBookingTo(booking: Booking, legacyStatus: string): void {
  appendBookingStateHistory(booking, legacyStatus);
  const states = bookingStateMachinesFor(legacyStatus);
  booking.bookingStatus = states.bookingStatus;
  booking.paymentState = states.paymentState;
  booking.mirState = states.mirState;
}

/**
 * Whether the seeded booking belongs to its origin channel, judged by the same shared rule
 * the screens use. The backend answers its guarded mutations on one of these with
 * 409 CHANNEL_BOOKING_READ_ONLY, and so do the mocks.
 */
export function isChannelManagedBookingMock(id: string): boolean {
  return isChannelManagedBooking(mockDb.getBookingById(id));
}

/** How a seeded booking answers a MIR reactivation request, once it has been skipped. */
type MirReactivationMode = 'sync' | 'async' | 'denied';

/** Reactivation scenario per booking, derived on skip and overridable for tests/demos. */
const reactivationScenario = new Map<string, MirReactivationMode>();
/** Bookings whose booking GET reports a MIR timeout risk (drives the pre-reactivation warning). */
const reactivationRisk = new Set<string>();
/** Booking GETs left before an in-progress (202) reactivation reports `manualMirManaged: false`. */
const reactivationPending = new Map<string, number>();
/** Booking GETs a 202 reactivation stays in progress for, so the client exercises its poll loop. */
const ASYNC_REACTIVATION_POLLS = 2;

/**
 * Picks a reactivation scenario (and timeout risk) for a booking the first time it is skipped,
 * keyed off its external code so different bookings demo 200, 202 and 409 without extra wiring.
 * An explicit `setMirReactivationScenario` always wins.
 */
function ensureReactivationScenario(booking: Booking): void {
  if (reactivationScenario.has(booking.id)) {
    return;
  }
  const code = booking.externalCodeNumber ?? 0;
  reactivationScenario.set(
    booking.id,
    code % 7 === 0 ? 'denied' : code % 2 === 0 ? 'async' : 'sync'
  );
  if (code % 3 === 0) {
    reactivationRisk.add(booking.id);
  }
}

/** Test/demo hook: pin a booking's reactivation scenario and whether its GET reports a timeout risk. */
export function setMirReactivationScenario(
  id: string,
  opts: { mode?: MirReactivationMode; risk?: boolean } = {}
): void {
  reactivationScenario.set(id, opts.mode ?? 'sync');
  reactivationPending.delete(id);
  if (opts.risk) {
    reactivationRisk.add(id);
  } else if (opts.risk === false) {
    reactivationRisk.delete(id);
  }
}

export function getBookingByIdMock(id: string): Booking | undefined {
  const booking = mockDb.getBookingById(id);
  if (!booking) {
    return undefined;
  }
  // An in-progress (202) reactivation clears after a few booking reads, so a client sees the poll
  // settle from `manualMirManaged: true` to `false`.
  const pending = reactivationPending.get(id);
  if (pending !== undefined) {
    if (pending <= 1) {
      reactivationPending.delete(id);
      manualMirBookings.delete(id);
    } else {
      reactivationPending.set(id, pending - 1);
    }
  }
  return {
    ...booking,
    manualMirManaged: manualMirBookings.has(id),
    mirReactivationTimeoutRisk: reactivationRisk.has(id),
  };
}

export function searchBookingsMock(
  filters:
    | {
        rentalId?: string;
        statuses?: string[];
        bookingStatus?: string;
        searchValue?: string;
        checkInDate?: string;
        checkOutDate?: string;
        page?: number;
        size?: number;
      }
    | Criteria = {}
): PagedResponse<Booking> {
  return mockDb.searchBookings(filters);
}

export function getAllBookingsMock(page = 0, size = 20): PagedResponse<Booking> {
  return mockDb.getAllBookings(page, size);
}

export function createBookingMock(data?: Partial<Booking>): Booking {
  const base = mockDb.bookings[0];
  return { ...base, id: `booking-${Date.now()}`, ...data };
}

export function updateBookingMock(id: string, data: Partial<Booking>): Booking {
  const existing = mockDb.getBookingById(id);
  const base = existing ?? mockDb.bookings[0];
  return { ...base, id, ...data };
}

export function updateBookingHolderMock(bookingId: string, holderData: object): null {
  const idx = mockDb.bookings.findIndex(b => b.id === bookingId);
  if (idx !== -1) {
    mockDb.bookings[idx] = {
      ...mockDb.bookings[idx],
      bookingHolder: holderData as Booking['bookingHolder'],
    };
  }
  return null;
}

export function deleteBookingMock(): { success: boolean } {
  return { success: true };
}

export function acceptBookingMock(id: string): null {
  const booking = mockDb.bookings.find(b => b.id === id);
  if (booking) {
    moveBookingTo(booking, 'CHECKIN_PENDING');
  }
  return null;
}

export function rejectBookingMock(id: string): null {
  const booking = mockDb.bookings.find(b => b.id === id);
  if (booking) {
    moveBookingTo(booking, 'ACCEPTANCE_CANCELED');
  }
  return null;
}

export function updateBookingStatusMock(bookingId: string, body: { bookingStatus: string }): null {
  const booking = mockDb.bookings.find(b => b.id === bookingId);
  if (booking) {
    moveBookingTo(booking, body.bookingStatus);
  }
  return null;
}

export function getBookingsByRentalIdMock(rentalId: string): Booking[] {
  return mockDb.getBookingsByRentalId(rentalId);
}

export function getBookingsByHostIdMock(): Booking[] {
  return mockDb.getBookingsByHostId();
}

export function cancelBookingMock(id: string): null {
  const booking = mockDb.bookings.find(b => b.id === id);
  if (booking) {
    moveBookingTo(booking, 'BOOKING_CANCELLED');
  }
  return null;
}

export function refundBookingMock(
  id: string,
  data: { originalAmount?: number; penaltyCharges?: number; refundReason?: string }
): null {
  const booking = mockDb.bookings.find(b => b.id === id);
  if (booking) {
    const refund = {
      refundReason: data.refundReason,
      penaltyCharges: data.penaltyCharges ?? 0,
      totalRefunded: (data.originalAmount ?? 0) - (data.penaltyCharges ?? 0),
    };
    booking.refunds = [...(booking.refunds ?? []), refund];
  }
  return null;
}

/** Bookings whose guest registration the host skipped in this mock session. */
const manualMirBookings = new Set<string>();

/**
 * GET /bookings/{id}/workflow: the seeded booking's capabilities read off its status matrix, and
 * the skip flag the backend keeps. A skipped booking stops being editable, as in the backend.
 */
export function getBookingWorkflowMock(id: string): BookingMirWorkflow | undefined {
  const booking = mockDb.getBookingById(id);
  if (!booking) {
    return undefined;
  }
  const manual = manualMirBookings.has(id);
  const access = bookingGuestAccess(booking);
  const active = !manual && access.edit;
  const canSend = active && access.send;
  return {
    paymentState: (booking.paymentState ?? null) as BookingMirWorkflow['paymentState'],
    mirState: manual
      ? BookingMirState.MIR_MANUALLY_MANAGED
      : ((booking.mirState ?? null) as BookingMirWorkflow['mirState']),
    manualMirManaged: manual,
    canEditGuests: active,
    canSubmitGuestData: canSend,
    canSendMir: canSend,
    canCancelLocally:
      !['CANCELLED_BY_GUEST', 'CANCELLED_BY_HOST', 'CANCELLED_BY_CHANNEL', 'FINISHED'].includes(
        booking.bookingStatus
      ) && booking.sourceSystem !== 'CHANNEX',
  };
}

/**
 * POST /bookings/{id}/workflow/manual-mir with `manualMirManaged: true`: idempotent skip, answers
 * with the new workflow view and fixes the booking's reactivation scenario.
 */
export function omitBookingMirMock(id: string): BookingMirWorkflow | undefined {
  const booking = mockDb.getBookingById(id);
  if (booking) {
    manualMirBookings.add(id);
    ensureReactivationScenario(booking);
  }
  return getBookingWorkflowMock(id);
}

/** The outcome of a reactivation request, mapped to an HTTP status by the interceptor. */
export interface MirReactivationResult {
  status: number;
  workflow?: BookingMirWorkflow;
  errorCode?: string;
}

/**
 * POST /bookings/{id}/workflow/manual-mir with `manualMirManaged: false`: 409 when the booking's
 * scenario forbids it, 202 while an async reactivation runs (the booking GET clears it after a few
 * reads), or 200 when it completes at once.
 */
export function reactivateBookingMirMock(id: string): MirReactivationResult {
  const booking = mockDb.getBookingById(id);
  if (!booking) {
    return { status: 404 };
  }
  const mode = reactivationScenario.get(id) ?? 'sync';
  if (mode === 'denied') {
    return { status: 409, errorCode: MIR_REACTIVATION_NOT_ALLOWED };
  }
  if (mode === 'async') {
    // Repeating an in-progress request must not restart it: keep the existing countdown.
    if (!reactivationPending.has(id)) {
      reactivationPending.set(id, ASYNC_REACTIVATION_POLLS);
    }
    return { status: 202, workflow: getBookingWorkflowMock(id) };
  }
  manualMirBookings.delete(id);
  return { status: 200, workflow: getBookingWorkflowMock(id) };
}
